import { CommonModule } from "@angular/common";
import { Component, computed, OnInit, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ButtonModule } from "primeng/button";
import { TabsModule } from "primeng/tabs";
import { NeedsAttentionTab } from "./components/needs-attention-tab/needs-attention-tab.component";
import { LocationsTabComponent } from "./components/locations-tab/locations-tab.component";
import { JobDetailFooterComponent } from "../../../../../domains/jobs/components/shared/job-detail-footer/job-detail-footer.component";
import ScheduleDetailHeader from "../../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { BottomSheetStateService } from "../../../../../shared/services/bottom-sheet-state.service";
import { InspectionItemSearchComponent, InspectionSearchItem } from "../../../BQ7/global-inspection-search/inspection-item-search.component";
import { BqStepDataService, BqStepDataServiceFactory } from "../../../../shared/bq-step-data-service/bq-step-data-service-factory";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { AreaWithDetails } from "../../../../../domains/jobs/services/bq-inspection-data.service";
import { AreaService } from "../../../../../domains/areas/services/area.service";
import { AreaInspectionSearchService } from "../../../../../domains/areas/services/area-inspection-search.service";
import { DataUpdatedFadeDirective } from "../../../../../shared/directives/data-updated-fade.directive";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";

export enum JobOverviewTabs {
  Locations = "locations",
  NeedsAttention = "needs-attention",
}

@Component({
  selector: "job-overview",
  providers: [BqStepDataServiceFactory],
  imports: [CommonModule,
    TabsModule,
    ButtonModule,
    ScheduleDetailHeader,
    JobDetailFooterComponent,
    NeedsAttentionTab,
    LocationsTabComponent,
    InspectionItemSearchComponent,
    DataUpdatedFadeDirective
  ],
  templateUrl: "./job-overview.component.html",
  styleUrls: ["./job-overview.component.scss"]
})
export class JobOverviewComponent implements OnInit {

  constructor(
    private router: ExtendedRouter,
    private route: ActivatedRoute,
    private areaService: AreaService,
    private areaInspectionSearchService: AreaInspectionSearchService,
    public bottomSheetState: BottomSheetStateService,
    public bqStepDataServiceFactory: BqStepDataServiceFactory,
    public toastService: UiToastService
  ) { }

  JobOverviewTabsEnum = JobOverviewTabs;

  tab = signal(JobOverviewTabs.Locations);
  bqStep = signal("");
  isEditing = signal(false);
  searchVisible = signal(false);
  dataLoaded = signal(false);

  private bqStepDataService = signal<BqStepDataService | null>(null);

  job = computed(() => this.bqStepDataService()?.job());
  lot = computed(() => this.bqStepDataService()?.lot());
  areas = computed(() => this.bqStepDataService()?.areas() ?? []);
  components = computed(() => this.bqStepDataService()?.components() ?? []);
  assertions = computed(() => this.bqStepDataService()?.assertions() ?? []);
  assertionIssues = computed(() => this.bqStepDataService()?.assertionIssues() ?? []);
  zones = computed(() => this.bqStepDataService()?.zones() ?? []);
  isCcmInspection = computed(() => this.bqStepDataService()?.isCcmInspection() ?? false);
  isRefreshingInBackground = computed(() => this.bqStepDataService()?.isRefreshingInBackground() ?? false);
  dataUpdatedPulse = computed(() => this.bqStepDataService()?.dataUpdatedPulse() ?? false);

  areasWithDetails = computed(() => {
    const areas = this.areas();
    const components = this.components();
    const assertions = this.assertions();
    const assertionIssues = this.assertionIssues();
    const zones = this.zones();
    const isCcmInspection = this.isCcmInspection();

    const areasWithDetails = areas.map(area => this.areaService.createAreaWithDetails(zones, area, components, assertions, assertionIssues, isCcmInspection));
    const sortedAreasWithDetails = this.sortAreasWithDetails(areasWithDetails);
    return sortedAreasWithDetails;
  });

  title = computed(() => this.lot()?.streetAddress ?? "");
  subtitle = computed(() => `${this.lot()?.salesCommunityName ?? ""} - ${this.lot()?.lotBlock ?? ""}`);
  backLabel = computed(() => (this.bqStepDataService()?.isCcmInspection() ? "Schedule" : "Summary"));

  completedAreaCount = computed(() => this.areasWithDetails().filter(a => a.isApplicable && a.completed).length);
  totalAreaCount = computed(() => this.areasWithDetails().filter(a => a.isApplicable).length);

  searchableInspectionItems = computed<InspectionSearchItem[]>(() => this.areaInspectionSearchService.buildFromAreaDetails(this.areasWithDetails()));

  async ngOnInit(): Promise<void> {
    const bqStep: string = this.route.snapshot.parent?.data["bqStep"];
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService(bqStep));

    const job = this.bqStepDataService()?.job();
    const lot = this.bqStepDataService()?.lot();
    const areas = this.bqStepDataService()?.areas();
    const zones = this.bqStepDataService()?.zones();
    const components = this.bqStepDataService()?.components();
    const assertions = this.bqStepDataService()?.assertions();
    const assertionIssues = this.bqStepDataService()?.assertionIssues();

    const hasData = !!job && !!lot && !!areas && !!zones && !!components && !!assertions && !!assertionIssues;
    if (hasData === false) {
      this.toastService.error(`Error loading job data for ${bqStep}. Please try again.`);
      this.onBack();
      return;
    }

    this.bqStep.set(bqStep);
    this.tab.set(this.route.snapshot.paramMap.get("activeTab") as JobOverviewTabs ?? JobOverviewTabs.Locations);

    this.dataLoaded.set(true);
  }

  onTabChange(index: string | number): void {
    this.tab.set(index as JobOverviewTabs);
    this.router.navigate(["..", this.tab()], {
      relativeTo: this.route,
      replaceUrl: true
    });
  }

  onOpenSearch(): void {
    this.searchVisible.set(true);

    setTimeout(() => {
      document.getElementById("inspection-search-input")?.focus();
    });
  }

  onCloseSearch(): void {
    this.searchVisible.set(false);
  }

  onNavigateToSearchResult(result: InspectionSearchItem): void {
    this.onCloseSearch();

    this.router.navigate(["../../area", result.areaId], {
      relativeTo: this.route,
      queryParams: {
        assertionIdentifier: result.assertionIdentifier,
        spotlight: true,
      },
      fragment: `assertion-${result.assertionIdentifier}`,
    });
  }

  onBack() {
    if (this.bqStepDataService()?.isCcmInspection()) {
      this.router.navigate(["../../../schedule"], { relativeTo: this.route.parent });
    } else {
      const communityId = this.bqStepDataService()?.lot()?.externalFinancialCommunityId ?? 0;
      const lotId = this.bqStepDataService()?.lot()?.id ?? 0;

      if (communityId === 0 || lotId === 0) {
        this.router.navigateWithPersistedState(["/cm/schedule"]);
      } else {
        this.router.navigateWithPersistedState([
          "/cm/all-my-lots/community", communityId, "lot", lotId, "summary"
        ]);
      }
    }
  }

  onFinalReviewClick(): void {
    this.router.navigate(["../../final-review"], { relativeTo: this.route });
  }

  sortAreasWithDetails(items: AreaWithDetails[]): AreaWithDetails[] {
    return [...items].sort(
      (a, b) =>
        (a.sortingOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.sortingOrder ?? Number.MAX_SAFE_INTEGER)
    );
  }
}
